import * as PIXI from 'pixi.js';

import gsap from 'gsap';

import { SkillExecutedEvent, CombatResult } from '@/core/types/battle.types';
import { BattleBus } from '@/events/Events';

/**
 * Damage Text Layer
 * 
 * Simple damage number display над units
 * - Event-driven (auto-shows on damage events)
 * - GSAP animations (no manual ticker needed)
 * - Different styles for normal/miss/crit
 * - No pooling (creates/destroys on demand)
 * 
 * Fonts:
 * - KyivType — для damage numbers (тематично відповідає сеттингу)
 * - Stroke компенсує відсутність bold в KyivType
 */

const metalGradient = new PIXI.FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0, color: 0xe6e9ec },
    { offset: 0.4, color: 0xa7b0b8 },
    { offset: 1, color: 0x5f666c },
  ],
});

const forgedGradient = new PIXI.FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0, color: 0xfff2c6 },
    { offset: 0.4, color: 0xe0a83a },
    { offset: 1, color: 0x6e3f10 },
  ],
});

const bloodGradient = new PIXI.FillGradient({
  type: 'linear',
  colorStops: [
    { offset: 0, color: 0x5f666c },
    { offset: 0.5, color: 0xa7b0b8 },
    { offset: 1, color: 0x5f666c },
  ],
});

const NORMAL_STYLE = new PIXI.TextStyle({
    fontFamily: 'Alegreya, serif',
    fontSize: 72,
    fontWeight: '500',
    letterSpacing: 1,

    fill: metalGradient,

    stroke: {
        color: 0x1b1e21,
        width: 8,  // товщий stroke замість bold
        join: 'round',
        miterLimit: 2,
    },

    dropShadow: {
        alpha: 0.6,
        angle: Math.PI / 4,
        blur: 6,
        color: 0x000000,
        distance: 4,
    },
});

const CRIT_STYLE = new PIXI.TextStyle({
    fontFamily: 'Alegreya, serif',
    fontSize: 84,  // більший розмір замість bold для драматичності
    fontWeight: '500',
    letterSpacing: 2,

    fill: forgedGradient,

    stroke: {
        color: 0x2b1a0b,
        width: 10,  // максимальний stroke для крит
        join: 'round',
        miterLimit: 2,
    },
    
    dropShadow: {
        alpha: 0.7,
        angle: Math.PI / 4,
        blur: 12,
        color: 0x000000,
        distance: 6,
    },
});

const MISS_STYLE = new PIXI.TextStyle({
    fontFamily: 'Alegreya, serif',
    fontSize: 64,
    fontWeight: '700',
    letterSpacing: 2,  // більший spacing для MISS — легший вигляд

    fill: bloodGradient,

    stroke: {
        color: 0x1b1e21,
        width: 5,
        join: 'round',
        miterLimit: 2,
    },

    dropShadow: {
        alpha: 0.4,
        angle: Math.PI / 4,
        blur: 6,
        color: 0x000000,
        distance: 4,
    },
});


export class DamageText extends PIXI.Container {
    constructor(
        private getUnitContainer: (unitId: string) => PIXI.Container | undefined
    ) {
        super();
        this.setupEventListeners();
    }

    private showDamangeBound = (data: SkillExecutedEvent) => this.showDamage(data);
    
    private setupEventListeners(): void {
        BattleBus.on('SKILL_EXECUTED', this.showDamangeBound);
    }
    
    /**
     * Show damage text above unit
     */
    private showDamage(event: SkillExecutedEvent): void {
        const unitContainer = this.getUnitContainer(event.targetId);
        if (!unitContainer) {
            console.warn(`[DamageText] Unit not found: ${event.targetId}`);
            return;
        }
        
        const text = this.createDamageText(event.result);
        
        text.position.set(
            unitContainer.x,
            unitContainer.y - 200
        );
        
        this.addChild(text);
        this.animateDamageText(text, event.result.isCrit || false, event.result.isMiss || false);
    }
    
    /**
     * Create PIXI.Text with style based on damage type
     */
    private createDamageText(data: CombatResult): PIXI.Text {
        let content: string;
        let style: Partial<PIXI.TextStyle>;
        
        if (data.isMiss) {
            content = 'MISS';
            style = MISS_STYLE;
        } else if (data.isCrit) {
            content = `${data.damage}`;
            style = CRIT_STYLE;
        } else {
            content = `${data.damage}`;
            style = NORMAL_STYLE;
        }

        const text = new PIXI.Text(content, style as PIXI.TextStyle);
        text.anchor.set(0.5);
        
        return text;
    }
    
    /**
     * Animate damage text with GSAP
     */
    private animateDamageText(text: PIXI.Text, isCrit: boolean, isMiss: boolean): void {
        const timeline = gsap.timeline();

        if (isMiss) {
            timeline.from(text.scale, { x: 0.9, y: 0.9, duration: 0.1 })
            .to(text, { y: text.y - 40, alpha: 0, duration: 1.1, ease: 'sine.out' });

        } else if (isCrit) {
            timeline.from(text.scale, { x: 2.4, y: 0.4, duration: 0.05 })
            .to(text.scale, { x: 1, y: 1, duration: 0.15 })
            .to(text, { x: '+=5', yoyo: true, repeat: 6, duration: 0.025 })
            .to(text, { y: text.y - 120, alpha: 0, duration: 1.7 });
                
        } else {
            timeline.from(text.scale, { x: 1.4, y: 0.6, duration: 0.08 })
            .to(text.scale, { x: 1, y: 1, duration: 0.1 })
            .to(text, { y: text.y + 6, duration: 0.05 })
            .to(text, { y: text.y - 70, alpha: 0, duration: 1.4, ease: 'power1.out' });
        }
        
        // Cleanup after animation
        timeline.call(() => {
            this.removeChild(text);
            text.destroy();
        });
    }
    
    /**
     * Cleanup
     */
    destroy(options?: boolean | PIXI.DestroyOptions): void {
        BattleBus.off('SKILL_EXECUTED', this.showDamangeBound);
        super.destroy(options);
    }
}